import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AuthUser = {
  id: string;
  email: string;
  displayName: string;
  // RFL-106: surfaced so the UI can defensively hide controls the server
  // would 403 anyway. Optional because sessions persisted before this
  // change won't carry them until the next /auth/me refresh or re-login.
  isSuperAdmin?: boolean;
  permissions?: string[];
  // RL-377: the feature modules this tenant is licensed for, from
  // /auth/me (and login). Optional for the same reason as the two above,
  // plus two of its own: an older server does not send the field at all,
  // and the instance plane has no tenant and therefore no entitlement.
  // See `useHasModule` for what an absent list means.
  modules?: string[];
};

type AuthState = {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  login: (user: AuthUser, accessToken: string | null) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;
};

/**
 * Auth store. Persists `{ user, accessToken }` to localStorage so a tab
 * refresh keeps the session. The token is a JWT issued by
 * /api/v1/auth/login with an 8-hour lifetime (RFL-205 — one retail
 * shift). Refresh-token rotation lands in Sprint 5 (TRS §28); until
 * then a 401 (expired/invalid token) boots the user back to /login.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      login: (user, accessToken) => set({ user, accessToken, isAuthenticated: true }),
      setUser: (user) => set({ user }),
      logout: () => set({ user: null, accessToken: null, isAuthenticated: false }),
    }),
    { name: 'reform-lite-auth' },
  ),
);

/**
 * Does the current user hold `code`? Super-admins implicitly hold every
 * permission (mirrors the backend RolesGuard). Defensive UX only — the
 * server-side `@Roles(...)` guard is the real enforcement boundary.
 */
export function useHasPermission(code: string): boolean {
  return useAuthStore((s) => hasPermission(s.user, code));
}

/** The rule behind `useHasPermission`, for callers that test many codes at once. */
export function hasPermission(user: AuthUser | null, code: string): boolean {
  return user?.isSuperAdmin === true || (user?.permissions ?? []).includes(code);
}

/**
 * Is this tenant licensed for the feature module `name`? (RL-377)
 *
 * A licence is not a permission. The server answers **404**, not 403, for
 * an unlicensed module, and it does so for super-admins too — so unlike
 * `useHasPermission` this hook has no super-admin bypass. Gating a nav
 * entry on `isSuperAdmin` would put `Kitchen orders` back in the sidebar
 * of every tenant that cannot open it, which is the bug this fixes.
 *
 * **An ABSENT list means "assume every module".** Deliberately fail-open,
 * and it is the only safe reading: `modules` is missing whenever the
 * server predates this field, whenever the session was persisted before
 * it, and for the moment between app start and the `/auth/me` refresh in
 * `RequireAuth`. Treating "I have not been told" as "licensed for
 * nothing" would empty the sidebar in all three cases — a UI that hides
 * pages the tenant paid for. An EMPTY list is different: that is the
 * server saying "nothing", and it hides accordingly. Nothing is lost by
 * failing open, because this is defensive UX only — the entitlement
 * guard, not this hook, is what actually withholds an unlicensed module.
 *
 * Only FEATURE modules appear in the list. Core surfaces (roles, i18n,
 * users, health) are always available and are not entitlement-gated on
 * the server, so never gate them on this hook — gate them on their
 * permission instead.
 */
export function useHasModule(name: string): boolean {
  return useAuthStore((s) => {
    const modules = s.user?.modules;
    if (modules === undefined) return true;
    // `core` is always on server-side (it is the auth surface a tenant
    // would have to reach to discover it was missing), so it never
    // appears in the entitlement list. Mirror that here.
    return name === 'core' || modules.includes(name);
  });
}
